import { useTranslation } from "react-i18next";
import { Grid2 } from "@mui/material";
import { InvestigationBlockInfo } from "../components/InvestigationBlockInfo";
import { InvolvedSectionTitle } from "./InvolvedSectionTitle";
import { useConstants } from "../Main/useConstants";
import { getSeniority, isEmployeeActive } from "./soeidRecord";
import { EMPLOYMENT_FIELDS } from "./employment";
import dayjs from "dayjs";

// Ficha de solo lectura de un involucrado, agrupada en los mismos bloques que
// el formulario. La comparten el desplegable de la lista y el drawer.
// columns: 2 aprovecha el ancho de la tarjeta de la lista; el drawer usa 1.
export const InvolvedDetails = ({
  involved,
  columns = 1,
  underline = true,
  spacing = 1.5,
}) => {
  const [t] = useTranslation("investigation");

  const {
    enums,
    functions,
    involveds: { colaboratorOpts, genderOpts, reinsidenceOpts },
  } = useConstants();

  // Interno => datos laborales; externo o sin definir => datos de la organizacion
  const isInternal = involved?.collaborator === "internal";

  const hasIdentification = involved?.collaborator || involved?.gender;
  // Resultado de "Validar SOEID"; se ignora si viniera en otro formato
  const validatedSoeid =
    involved?.soeid && typeof involved.soeid === "object"
      ? involved.soeid
      : null;

  // La antiguedad se calcula al vuelo: lo guardado es la fecha de ingreso
  const seniority = getSeniority(
    validatedSoeid?.hire_date,
    validatedSoeid?.resignation_date
      ? new Date(validatedSoeid.resignation_date)
      : undefined
  );
  const seniorityLabel = seniority
    ? [
        seniority.years > 0 &&
          t("involved.soeid_seniority_years", { count: seniority.years }),
        seniority.months > 0 &&
          t("involved.soeid_seniority_months", { count: seniority.months }),
      ]
        .filter(Boolean)
        .join(", ")
    : "";

  // Antiguedad de la fecha capturada a mano (la del registro de RH va aparte)
  const manualSeniority = getSeniority(involved?.hire_date);
  const manualSeniorityLabel = manualSeniority
    ? [
        manualSeniority.years > 0 &&
          t("involved.soeid_seniority_years", { count: manualSeniority.years }),
        manualSeniority.months > 0 &&
          t("involved.soeid_seniority_months", {
            count: manualSeniority.months,
          }),
      ]
        .filter(Boolean)
        .join(", ")
    : "";

  const hasEmployment =
    EMPLOYMENT_FIELDS.some((field) => involved?.[field]) || validatedSoeid;
  const hasOrganization =
    involved?.business_name || involved?.client_number || involved?.rfc;

  const blockSize = columns === 2 ? { xs: 12, sm: 6 } : 12;
  const blockProps = { size: blockSize, underline };

  return (
    <Grid2 container spacing={spacing} alignItems="flex-start">
      {/* 1. Quien es */}
      {hasIdentification && (
        <Grid2 size={12}>
          <InvolvedSectionTitle
            icon="outline_profile_circle"
            text={t("involved.section_identification")}
          />
        </Grid2>
      )}
      {involved?.collaborator && (
        <InvestigationBlockInfo
          {...blockProps}
          label={t("involved.collaborator_type")}
          text={
            colaboratorOpts.find((item) => item.value === involved?.collaborator)
              ?.label ?? ""
          }
        />
      )}
      {involved?.gender && (
        <InvestigationBlockInfo
          {...blockProps}
          label={t("involved.gender")}
          text={
            genderOpts.find((item) => item.value === involved?.gender)?.label ??
            ""
          }
        />
      )}

      {/* 2. De donde viene */}
      {isInternal
        ? hasEmployment && (
            <>
              <Grid2 size={12}>
                <InvolvedSectionTitle
                  icon="outline_briefcase"
                  text={t("involved.section_employment")}
                />
              </Grid2>
              {involved?.additional && (
                <InvestigationBlockInfo
                  {...blockProps}
                  label={t("involved.employee_id")}
                  text={involved?.additional ?? ""}
                />
              )}
              {involved?.area && (
                <InvestigationBlockInfo
                  {...blockProps}
                  label={t("involved.area")}
                  text={involved?.area ?? ""}
                />
              )}
              {involved?.relationship && (
                <InvestigationBlockInfo
                  {...blockProps}
                  label={t("involved.position")}
                  text={involved?.relationship ?? ""}
                />
              )}
              {[
                "level",
                "segment",
                "branch",
                "legal_vehicle",
                "manager",
                "corporate_email",
                "extension",
              ].map(
                (field) =>
                  involved?.[field] && (
                    <InvestigationBlockInfo
                      key={field}
                      {...blockProps}
                      label={t(`involved.${field}`)}
                      text={involved[field]}
                    />
                  )
              )}
              {involved?.hire_date && (
                <InvestigationBlockInfo
                  {...blockProps}
                  label={t("involved.hire_date")}
                  text={`${dayjs(involved.hire_date)
                    .format("DD/MMM/YYYY")
                    .toLocaleUpperCase()}${
                    manualSeniorityLabel ? ` · ${manualSeniorityLabel}` : ""
                  }`}
                />
              )}
              {validatedSoeid && (
                <>
                  <InvestigationBlockInfo
                    {...blockProps}
                    label={t("involved.soeid_validated")}
                    text={[validatedSoeid.soeid, validatedSoeid.full_name]
                      .filter(Boolean)
                      .join(" · ")}
                    icon="outline_user_tick"
                  />
                  {validatedSoeid.position_code && (
                    <InvestigationBlockInfo
                      {...blockProps}
                      label={t("involved.soeid_position")}
                      text={validatedSoeid.position_code}
                    />
                  )}
                  {validatedSoeid.office_code && (
                    <InvestigationBlockInfo
                      {...blockProps}
                      label={t("involved.soeid_office")}
                      text={validatedSoeid.office_code}
                    />
                  )}
                  {validatedSoeid.company && (
                    <InvestigationBlockInfo
                      {...blockProps}
                      label={t("involved.soeid_company")}
                      text={validatedSoeid.company}
                    />
                  )}
                  {validatedSoeid.hire_date && (
                    <InvestigationBlockInfo
                      {...blockProps}
                      label={t("involved.soeid_hire_date")}
                      text={`${dayjs(validatedSoeid.hire_date)
                        .format("DD/MMM/YYYY")
                        .toLocaleUpperCase()}${
                        seniorityLabel ? ` · ${seniorityLabel}` : ""
                      }`}
                    />
                  )}
                  {(validatedSoeid.manager || validatedSoeid.manager_geid) && (
                    <InvestigationBlockInfo
                      {...blockProps}
                      label={
                        validatedSoeid.manager
                          ? t("involved.soeid_manager")
                          : t("involved.soeid_manager_geid")
                      }
                      text={
                        validatedSoeid.manager
                          ? [
                              validatedSoeid.manager.soeid,
                              validatedSoeid.manager.full_name,
                            ]
                              .filter(Boolean)
                              .join(" · ")
                          : validatedSoeid.manager_geid
                      }
                    />
                  )}
                  {/* Una baja cambia el manejo del caso: no hay entrevista ni
                      sancion posible, asi que se avisa en la ficha. */}
                  {!isEmployeeActive(validatedSoeid) && (
                    <InvestigationBlockInfo
                      {...blockProps}
                      label={t("involved.soeid_employee_status")}
                      text={
                        validatedSoeid.resignation_date
                          ? `${t("involved.soeid_status_inactive")} · ${dayjs(
                              validatedSoeid.resignation_date
                            )
                              .format("DD/MMM/YYYY")
                              .toLocaleUpperCase()}`
                          : validatedSoeid.status ?? ""
                      }
                    />
                  )}
                </>
              )}
            </>
          )
        : hasOrganization && (
            <>
              <Grid2 size={12}>
                <InvolvedSectionTitle
                  icon="buildings"
                  text={t("involved.section_organization")}
                />
              </Grid2>
              {involved?.business_name && (
                <InvestigationBlockInfo
                  {...blockProps}
                  label={t("involved.business_name")}
                  text={involved?.business_name ?? ""}
                />
              )}
              {involved?.client_number && (
                <InvestigationBlockInfo
                  {...blockProps}
                  label={t("involved.client_number")}
                  text={involved?.client_number ?? ""}
                />
              )}
              {involved?.rfc && (
                <InvestigationBlockInfo
                  {...blockProps}
                  label={t("involved.rfc")}
                  text={involved?.rfc ?? ""}
                />
              )}
            </>
          )}

      {/* 3. Como contactarlo */}
      {involved?.contact_method && (
        <>
          <Grid2 size={12}>
            <InvolvedSectionTitle
              icon="call"
              text={t("involved.section_contact")}
            />
          </Grid2>
          <InvestigationBlockInfo
            {...blockProps}
            label={t("involved.contact_method")}
            text={involved?.contact_method ?? ""}
          />
        </>
      )}

      {/* 4. Su situacion dentro del caso */}
      <Grid2 size={12}>
        <InvolvedSectionTitle
          icon="outline_clipboard_task"
          text={t("involved.section_case")}
        />
      </Grid2>
      <InvestigationBlockInfo
        {...blockProps}
        label={t("involved.indicated_by")}
        text={
          involved?.indicated?.from === involved?.added
            ? functions.getUserLabel({ from: enums.typeUser.COMPLAINANT })
            : functions.getUserLabel(involved?.indicated) ?? t("involved.other")
        }
      />
      {involved?.reinsidence && (
        <InvestigationBlockInfo
          {...blockProps}
          label={t("involved.reinsidence")}
          text={
            reinsidenceOpts.find((item) => item.value === involved?.reinsidence)
              ?.label ?? ""
          }
        />
      )}
      {/* Los detalles son texto largo: siempre a todo lo ancho */}
      {involved?.details && (
        <InvestigationBlockInfo
          size={12}
          underline={underline}
          label={t("involved.details")}
          text={involved?.details ?? ""}
        />
      )}
    </Grid2>
  );
};
